import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { ArticleRequest } from '@core/interfaces/article-request.interface';
import { AuthService } from '@core/services/auth.service';
import { ModalUtilsService } from '@core/services/modal-utils.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faHeart as faHeartRegular } from '@fortawesome/free-regular-svg-icons';
import {
  faCheck,
  faCheckCircle,
  faExclamation,
  faHeart,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { LanguageService } from '@shared/services/language.service';
import { RedirectService } from '@shared/services/redirect.service';
import { Auction } from 'src/app/articles-manager/interfaces/auction.interface';
import { ArticleService } from 'src/app/articles-manager/services/article.service';
import { AuctionTypeInfoDialogComponent } from '../dialogs/auction-type-info-dialog/auction-type-info-dialog.component';

@Component({
  selector: 'app-article-properties',
  templateUrl: './article-properties.component.html',
  styleUrls: ['./article-properties.component.scss'],
  standalone: true,
  imports: [NgIf, NgFor, NgClass, RouterModule, FaIconComponent, TranslatePipe],
})
@UntilDestroy()
export class ArticlePropertiesComponent implements OnInit {
  faHeartRegular = faHeartRegular;
  faHeart = faHeart;
  faCheckCircle = faCheckCircle;
  faTimesCircle = faTimesCircle;
  faExclamation = faExclamation;
  faCheck = faCheck;

  selectedAuction = input<Auction>();
  isCertificatePage = input(false);
  isMessagePage = input(false);
  sportIds = input<string>();
  competitionIds = input<string>();
  teamIds = input<string>();
  inPreEventStatus = input(false);
  isPreEventFinished = input(false);

  isMemorabid = computed(() => this.selectedAuction().idAuctionType == 1);

  private readonly articleService = inject(ArticleService);

  articleRequest = computed(
    () =>
      ({
        idArticle: this.selectedAuction().article.idArticle,
      } as ArticleRequest)
  );
  title = computed(
    () => this.selectedAuction()?.article?.articleInfoList?.[0]?.title
  );

  isFavorite = signal(false);

  public authService: AuthService = inject(AuthService);
  private modalUtils: ModalUtilsService = inject(ModalUtilsService);
  private redirectService: RedirectService = inject(RedirectService);
  protected languageService: LanguageService = inject(LanguageService);

  constructor() {
    effect(
      (onCleanup) => {
        const sub = this.articleService
          .isFavorite(this.articleRequest().idArticle)
          .pipe(untilDestroyed(this))
          .subscribe((isFavorite) => {
            this.isFavorite.set(isFavorite);
          });

        onCleanup(() => {
          sub.unsubscribe();
        });
      },
      {
        allowSignalWrites: true,
      }
    );
  }

  ngOnInit(): void {}

  openAuctionInfo() {
    const ref = this.modalUtils.openModal(AuctionTypeInfoDialogComponent, {
      size: 'lg',
      keyboard: false,
    });
    ref.componentInstance.isMemorabid = this.isMemorabid();
    ref.componentInstance.inPreEventStatus =
      this.selectedAuction().isPreEvent && !this.selectedAuction().eventIsOpen;
  }

  addToFavorites(event: Event) {
    event.stopPropagation();
    event.preventDefault();

    this.articleService
      .save(this.articleRequest())
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  removeFromFavorites() {
    this.articleService
      .delete(this.articleRequest())
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  goToPageCampaign() {
    this.redirectService.redirect(
      this.languageService.userSelectedLanguage.code +
        '/campaign/' +
        this.selectedAuction().idCampaign
    );
  }

  goToPagePromoter() {
    this.redirectService.redirect(
      this.languageService.userSelectedLanguage.code +
        '/promoter/' +
        this.selectedAuction().idPromoter
    );
  }
}
